import React, { useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { Users, TrendingUp, Clock, UserCheck } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { format, subMonths, subDays } from 'date-fns';
import { ko } from 'date-fns/locale';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: {p.value}{p.unit || ''}</p>
        ))}
      </div>
    );
  }
  return null;
};

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="card p-5">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
      <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
    </div>
  </div>
);

const Analytics = () => {
  const { members, attendances } = useApp();

  // 월별 신규 회원 수 (최근 12개월)
  const memberGrowthData = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const d = subMonths(new Date(), 11 - i);
      const monthStr = format(d, 'yyyy-MM');
      const count = members.filter(m => m.createdAt?.startsWith(monthStr)).length;
      return { label: format(d, 'M월', { locale: ko }), count };
    });
  }, [members]);

  // 월별 출석 수 (최근 12개월)
  const attendanceMonthlyData = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const d = subMonths(new Date(), 11 - i);
      const monthStr = format(d, 'yyyy-MM');
      const count = attendances.filter(a => a.date?.startsWith(monthStr)).length;
      return { label: format(d, 'M월', { locale: ko }), count };
    });
  }, [attendances]);

  // 최근 30일 일별 출석 수
  const dailyAttendanceData = useMemo(() => {
    return Array.from({ length: 30 }, (_, i) => {
      const d = subDays(new Date(), 29 - i);
      const dateStr = format(d, 'yyyy-MM-dd');
      const count = attendances.filter(a => a.date === dateStr).length;
      return {
        label: format(d, 'M/d'),
        count,
        day: format(d, 'EEE', { locale: ko }),
      };
    });
  }, [attendances]);

  // 시간대별 출석 분포
  const hourlyData = useMemo(() => {
    const hours = Array.from({ length: 14 }, (_, i) => ({ hour: i + 6, count: 0 })); // 6~19시
    attendances.forEach(a => {
      if (!a.time) return;
      const h = parseInt(a.time.split(':')[0]);
      const idx = h - 6;
      if (idx >= 0 && idx < 14) hours[idx].count++;
    });
    return hours.map(h => ({ label: `${h.hour}시`, count: h.count }));
  }, [attendances]);

  // 회원 상태 분포
  const statusDist = useMemo(() => {
    const map = { active: 0, expired: 0, inactive: 0 };
    members.forEach(m => { if (map[m.status] !== undefined) map[m.status]++; });
    return [
      { name: '활성', value: map.active, color: '#10b981' },
      { name: '만료', value: map.expired, color: '#ef4444' },
      { name: '휴면', value: map.inactive, color: '#6b7280' },
    ];
  }, [members]);

  // 운동목적 분포
  const purposeDist = useMemo(() => {
    const map = {};
    members.forEach(m => { if (m.purpose) map[m.purpose] = (map[m.purpose] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [members]);

  // 알게된경로 분포
  const referralDist = useMemo(() => {
    const map = {};
    members.forEach(m => { if (m.referral) map[m.referral] = (map[m.referral] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [members]);

  const today = format(new Date(), 'yyyy-MM-dd');
  const todayCount = attendances.filter(a => a.date === today).length;
  const avgMonthly = Math.round(attendanceMonthlyData.slice(-3).reduce((s, d) => s + d.count, 0) / 3);
  const activeCount = members.filter(m => m.status === 'active').length;
  const retentionRate = members.length > 0 ? Math.round((activeCount / members.length) * 100) : 0;

  const COLORS_CHART = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];

  return (
    <div className="space-y-6">
      {/* 요약 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="전체 회원" value={`${members.length}명`} color="text-blue-600 dark:text-blue-400" sub={`활성 ${activeCount}명`} />
        <StatCard icon={TrendingUp} label="오늘 출석" value={`${todayCount}명`} color="text-green-600 dark:text-green-400" sub="오늘 기준" />
        <StatCard icon={Clock} label="월평균 출석" value={`${avgMonthly}회`} color="text-purple-600 dark:text-purple-400" sub="최근 3개월" />
        <StatCard icon={UserCheck} label="회원 유지율" value={`${retentionRate}%`} color="text-orange-600 dark:text-orange-400" sub="활성 / 전체" />
      </div>

      {/* 차트 섹션 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 월별 출석 추이 */}
        <div className="card p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">월별 출석 추이</h2>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={attendanceMonthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.5} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="count" name="출석 수" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} unit="회" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* 월별 신규 회원 */}
        <div className="card p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">월별 신규 회원</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={memberGrowthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.5} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="신규 회원" fill="#10b981" radius={[3, 3, 0, 0]} unit="명" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 최근 30일 일별 출석 */}
        <div className="card p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">최근 30일 일별 출석</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dailyAttendanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.5} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval={4} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="출석" fill="#8b5cf6" radius={[2, 2, 0, 0]} unit="명" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 시간대별 출석 분포 */}
        <div className="card p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">시간대별 출석 분포</h2>
          {hourlyData.every(d => d.count === 0) ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">데이터가 없습니다.</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.5} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="출석" radius={[3, 3, 0, 0]} unit="명">
                  {hourlyData.map((entry, i) => {
                    const max = Math.max(...hourlyData.map(d => d.count));
                    const intensity = max > 0 ? entry.count / max : 0;
                    return <Cell key={i} fill={intensity === 1 ? '#ef4444' : intensity > 0.7 ? '#f59e0b' : '#3b82f6'} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* 분포 분석 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* 회원 상태 */}
        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">회원 상태 분포</h3>
          <div className="space-y-3">
            {statusDist.map(d => (
              <div key={d.name}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600 dark:text-gray-400">{d.name}</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{d.value}명 ({members.length > 0 ? Math.round(d.value / members.length * 100) : 0}%)</span>
                </div>
                <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                  <div className="h-2 rounded-full transition-all" style={{ width: `${members.length > 0 ? (d.value / members.length) * 100 : 0}%`, backgroundColor: d.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 운동 목적 */}
        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">운동 목적</h3>
          <div className="space-y-2">
            {purposeDist.length === 0 ? <p className="text-sm text-gray-400">데이터 없음</p> : purposeDist.map((d, i) => (
              <div key={d.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS_CHART[i % COLORS_CHART.length] }} />
                  <span className="text-sm text-gray-600 dark:text-gray-400">{d.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full">
                    <div className="h-1.5 rounded-full" style={{ width: `${(d.value / members.length) * 100}%`, backgroundColor: COLORS_CHART[i % COLORS_CHART.length] }} />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-6">{d.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 알게된 경로 */}
        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">알게된 경로</h3>
          <div className="space-y-2">
            {referralDist.length === 0 ? <p className="text-sm text-gray-400">데이터 없음</p> : referralDist.map((d, i) => (
              <div key={d.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS_CHART[i % COLORS_CHART.length] }} />
                  <span className="text-sm text-gray-600 dark:text-gray-400">{d.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full">
                    <div className="h-1.5 rounded-full" style={{ width: `${(d.value / members.length) * 100}%`, backgroundColor: COLORS_CHART[i % COLORS_CHART.length] }} />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-6">{d.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
